import {Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Carrier, IE3CL707, IE3Communication, IE3FilingType} from "@portbase/bezoekschip-service-typescriptmodels";
import {PortvisitUtils} from "../../../../../refdata/portvisit-utils";
import {ConsignmentUtils} from "../../../consignment.utils";
import lodash, {cloneDeep} from "lodash";
import {checkValidity, clearValidation} from "../../../../../common/utils";
import countryValidations from "../consignment-party-details/country-validation.json";
import {HandleEvent} from "../../../../../common/handlers/handle-event";
import {View} from "../../../../../common/handlers/view";
import {Handler} from "../../../../../common/handlers/handler";
import {ConsignmentRules} from "../../../consignment.rules";
import {ErrorWithPointers} from "../../../../../common/errors/errors";
import {HandleQuery} from "../../../../../common/handlers/handle-query";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";

@Component({
    selector: 'app-consignment-carrier-details',
    templateUrl: './consignment-carrier-details.component.html',
    styleUrls: ['./consignment-carrier-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Handler()
export class ConsignmentCarrierDetailsComponent extends View implements OnInit {
  portVisitUtils = PortvisitUtils;
  utils = ConsignmentUtils;
  rules = ConsignmentRules;
  elementRef: ElementRef = inject(ElementRef);

  @Input() cargoDeclarantId: string;
  @Input() label: string;
  carrier: Carrier;
  @Input() editMode: boolean;
  @Input() required: boolean;
  // Undefined keeps the default (ENS-strict) address and telephone rules.
  @Input() filingType: IE3FilingType;
  validations: CountryValidation[] = countryValidations as CountryValidation[];

  countryValidation: CountryValidation;

  carrierEditMode: boolean;
  backupCarrier: Carrier;
  defaultTelephone: IE3Communication = { type: IE3CL707.TE };
  defaultEmail: IE3Communication = { type: IE3CL707.EM };

  @Output() carrierChange: EventEmitter<Carrier> = new EventEmitter<Carrier>();

  @Input("carrier")
  set setCarrier(carrier: Carrier) {
    this.carrier = this.initialiseCarrier(carrier);
  }

  ngOnInit() {
    if (this.carrier) {
      this.updateCountryValidation();
    }
  }

  get streetRequired() {
    return this.rules.isPartyAddressDetailRequired(this.filingType) && !this.carrier.address.poBox;
  }

  get poBoxRequired() {
    return this.rules.isPartyAddressDetailRequired(this.filingType)
      && (!this.carrier.address.street || !this.carrier.address.number);
  }

  get postCodeRequired() {
    return this.rules.isPartyAddressDetailRequired(this.filingType)
      && ['POSTAL_CODE', 'POSTAL_CODE_AND_STREET'].includes(this.countryValidation?.validationCode);
  }

  get isTsdFiling() {
    return this.rules.isTemporaryStorageOnlyConsignment(this.filingType);
  }

  @HandleEvent("validationFailed", { global: true })
  validationFailed() {
    if (!checkValidity(this.elementRef)) {
      this.carrierEditMode = true;
    }
  }

  get telephone() {
    return this.carrier.communications.find(c => c.type === IE3CL707.TE) || this.defaultTelephone;
  }

  get email() {
    return this.carrier.communications.find(c => c.type === IE3CL707.EM) || this.defaultEmail;
  }

  onCountryChange(country: string) {
    if (country) {
      this.updateCountryValidation();
    }
  }

  updateCountryValidation = () => {
    this.countryValidation = this.carrier.address.country
      ? this.validations.find(c => c.countryCode === this.carrier.address.country) : null;
  }

  toggleEditMode = () => {
    this.carrierEditMode = !this.carrierEditMode;
    if (this.carrierEditMode) {
      this.backupCarrier = cloneDeep(this.carrier);
    }
  }

  cancel = () => {
    this.carrier = this.backupCarrier;
    this.toggleEditMode();
    clearValidation(this.elementRef);
  }

  save = () => {
    this.carrier.communications = this.carrier.communications.filter(c => c.identifier);
    if (checkValidity(this.elementRef)) {
      this.processModel();
      this.toggleEditMode();
      this.carrierChange.emit(this.carrier);
    }
  }

  processModel = () => {
    if (!this.carrier.communications?.find(c => c.type === IE3CL707.TE) && this.defaultTelephone.identifier) {
      this.carrier.communications.push(this.defaultTelephone);
    }
    if (!this.carrier.communications?.find(c => c.type === IE3CL707.EM) && this.defaultEmail.identifier) {
      this.carrier.communications.push(this.defaultEmail);
    }
    this.carrier.communications = this.carrier.communications?.filter(c => c.identifier) || [];
  }

  newCarrier = (value: string): Carrier => {
    const carrier: Carrier = this.initialiseCarrier({
      name: value
    });
    carrier["isNewRecord"] = true;
    return carrier;
  }

  onCarrierChange = (value: Carrier) => {
    if (value?.scacCode === "XXXX") {
       value = this.newCarrier(value.name);
    }
    this.carrier = this.initialiseCarrier(value);
    if (value && value["isNewRecord"]) {
      this.carrierEditMode = true;
    }
    if (!this.carrierEditMode) {
      this.carrierChange.emit(this.carrier);
    }
  }

  private initialiseCarrier(carrier: Carrier) {
    if (carrier == null) {
      return null;
    }
    carrier.address = carrier.address || {};
    carrier.communications = carrier.communications || [];
    carrier.boxOperators = carrier.boxOperators || [];
    return carrier;
  }

  @HandleQuery("getErrors")
  getErrors(): Observable<ErrorWithPointers[]> {
    return this.sendQuery("getConsignmentErrors")
      .pipe(map((e: ErrorWithPointers[]) => {
        const predicate = p => p.model === this.carrier;
        return e.filter(p => p.pointers.some(predicate))
          .map(e => ({
            ...e,
            pointers: lodash.drop(e.pointers, lodash.findIndex(e.pointers, predicate))
          }));
      }));
  }
}

interface CountryValidation {
  countryCode: string;
  validationCode: "POSTAL_CODE_AND_STREET" | "POSTAL_CODE" | "COUNTRY_ONLY";
}
